import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment";

import ArialNormal from "../../../utils/Customfonts/ARIAL.TTF";
import ArialBold from "../../../utils/Customfonts/ARIALBD.TTF";
import ArialItalic from "../../../utils/Customfonts/ARIALI.TTF";
import ArialBoldItalic from "../../../utils/Customfonts/ARIALBI.TTF";

const DYC_LOGO = "/img/dycgpl-logo.webp";

export const customFontFamily = {
  bold: "bold",
  normal: "normal",
  boldFont: "Arial",
  normalFont: "Arial",
  italic: "italic",
  italicFont: "Arial",
};

// -----------------------------------------------------------------------------
// GLOBAL LAYOUT CONFIG (same pattern as the CV generator)
// -----------------------------------------------------------------------------

export const config = {
  borderMargin: 15,
  contentPadding: 20,

  get margin() {
    return this.borderMargin + this.contentPadding;
  },

  fontSize: 9,
  fontSizeSm: 8,
  fontSizeLg: 11,
  fontSizeTitle: 18,

  cellPadding: 5,

  sectionGap: 16,
  lineGap: 16,

  headerFontSize: 9.5,
  headerFont: customFontFamily.boldFont,
  headerFontStyle: customFontFamily.bold,
  headerAlign: "center",
  headerPadding: { top: 6, right: 6, bottom: 6, left: 6 },

  get contentTop() {
    return this.borderMargin + 65;
  },

  get contentBottom() {
    return this.borderMargin + 25;
  },
};

// -----------------------------------------------------------------------------
// FIXED FORM OPTIONS (always printed in full; ticked based on `data`)
// -----------------------------------------------------------------------------

const RATING_COLUMNS = [
  { key: "V. Good", sub: "Exceeds requirement" },
  { key: "Good", sub: "Meets all requirement" },
  { key: "Average", sub: "Meets some requirement" },
  { key: "Poor", sub: "Does not meet requirements" },
];

// First table: "RATINGS / ATTRIBUTES"
const TECHNICAL_ATTRIBUTE_ROWS = [
  {
    label: "TECHNICAL/JOB KNOWLEDGE",
    description:
      "Please rate candidate basis his/her relevant technical or functional knowledge to perform the applied job – Please refer JD for the technical competencies",
    stateKey: "technicalJobKnowledge",
    remarkKey: "technicalJobKnowledgeRemarks",
  },
  {
    label: "RELEVANT EXPERIENCE",
    description:
      "Please rate candidate basis his/her level of expertise, demonstrated ability to perform the applied job",
    stateKey: "relevantExperience",
    remarkKey: "relevantExperienceRemarks",
  },
  {
    label: "COMMUNICATION",
    description:
      "Please rate candidate basis his/her ability to communicate thoughts & ideas clearly NOT basis the fluency or command over the language.",
    stateKey: "communication",
    remarkKey: "communicationRemarks",
  },
  {
    label: "*ANALYTICAL SKILLS",
    description:
      "Rate basis how candidate analyzed the problem - ability to collect and process information or data, comprehend the issue, using the same to analyze the root cause of an issue or a problem.",
    stateKey: "analyticalSkills",
    remarkKey: "analyticalSkillsRemarks",
  },
  {
    label: "* PROBLEM SOLVING SKILLS",
    description:
      "Rate basis how candidate responded and solved the problem – Look for the approach calm or excited / blaming or resolving / provided Solutions or ignored or escalated",
    stateKey: "problemSolvingSkills",
    remarkKey: "problemSolvingSkillsRemarks",
  },
  {
    label: "*INITIATIVE SKILLS",
    description:
      "Ask candidate if they have taken initiative to complete any task or project in current or earlier assignments when no or fewer directions were available. OR if they have worked on completely new challenging task or project voluntarily.",
    stateKey: "initiativeSkills",
    remarkKey: "initiativeSkillsRemarks",
  },
];

// Second table: "BEHAVIOURAL COMPETENCIES CORRESPONDING TO DYC GLOBAL VALUES"
const BEHAVIOURAL_ATTRIBUTE_ROWS = [
  {
    label: "DEPENDABLE",
    description:
      "Please rate the candidate on demonstrated dependability to complete a task in adverse condition – in their current & previous roles. How did they meet the timelines and manage expectations of stakeholders?",
    stateKey: "dependable",
    remarkKey: "dependableRemarks",
  },
  {
    label: "*ACCOUNTABLE",
    description:
      "Please rate the candidate on demonstrated ability of ownership & accountability in their current & previous roles.",
    stateKey: "accountable",
    remarkKey: "accountableRemarks",
  },
  {
    label: "*PEOPLE MANAGEMENT",
    description:
      "Please rate candidate on demonstrated ability to manage / Lead People and work as a team in their current & previous roles.",
    stateKey: "peopleManagement",
    remarkKey: "peopleManagementRemarks",
  },
  {
    label: "DRIVE FOR ACHIEVEMENT",
    description:
      "Please rate candidate basis his/her Achievement drive and willingness to GROW / commitment to Individual development.",
    stateKey: "driveForAchievement",
    remarkKey: "driveForAchievementRemarks",
  },
];

const JOB_FIT_OPTIONS = ["GOOD", "AVERAGE", "POOR"];

const RECOMMENDATION_OPTIONS = [
  { key: "Rejected", label: "Rejected" },
  { key: "Shortlisted", label: "Shortlisted for next Round" },
  { key: "Selected", label: "Selected" },
];

const GRADE_OPTIONS = [
  {
    key: "A",
    label: "Grade A - Proficient in training, monitoring, guiding, and evaluating fellow Employees",
  },
  {
    key: "B",
    label: "Grade B - Competent to work autonomously and provide guidance to others",
  },
  {
    key: "C",
    label: "Grade C - Capable of working independently under the guidance of senior professionals",
  },
  {
    key: "D",
    label: "Grade D - Able to perform tasks under supervision.",
  },
];

const PANEL_ROWS = 5;

// -----------------------------------------------------------------------------
// COMMON STYLES
// -----------------------------------------------------------------------------

const getBaseStyles = () => ({
  fontSize: config.fontSize,
  cellPadding: config.cellPadding,
  lineColor: [0, 0, 0],
  lineWidth: 0.5,
  textColor: [0, 0, 0],
  fillColor: false,
  font: customFontFamily.normalFont,
  fontStyle: customFontFamily.normal,
  halign: "left",
  valign: "middle",
});

const getHeaderStyles = () => ({
  ...getBaseStyles(),
  font: customFontFamily.boldFont,
  fontStyle: customFontFamily.bold,
  fillColor: [225, 225, 225],
  textColor: [0, 0, 0],
  halign: "center",
  valign: "middle",
});

const getSubHeaderStyles = () => ({
  ...getBaseStyles(),
  font: customFontFamily.italicFont,
  fontStyle: customFontFamily.italic,
  fontSize: config.fontSizeSm,
  fillColor: [245, 245, 245],
  textColor: [0, 0, 0],
  halign: "center",
  valign: "middle",
});

// -----------------------------------------------------------------------------
// LAYOUT MANAGER
// -----------------------------------------------------------------------------

const layout = {
  doc: null,
  y: 0,
  pageWidth: 0,
  pageHeight: 0,
  contentWidth: 0,

  init(doc) {
    this.doc = doc;
    this.pageWidth = doc.internal.pageSize.getWidth();
    this.pageHeight = doc.internal.pageSize.getHeight();
    this.contentWidth = this.pageWidth - config.margin * 2;
    this.y = config.contentTop;
  },

  advance(amount) {
    this.y += amount;
  },

  ensureSpace(requiredHeight = 40) {
    if (this.y + requiredHeight > this.pageHeight - config.contentBottom) {
      this.doc.addPage();
      this.y = config.contentTop;
    }
  },

  afterTable(gap = config.sectionGap) {
    if (this.doc.lastAutoTable) {
      this.y = this.doc.lastAutoTable.finalY + gap;
    }
  },
};

// -----------------------------------------------------------------------------
// FONT LOADING
// -----------------------------------------------------------------------------

const arrayBufferToBase64 = (buffer) => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const chunkSize = 8192;

  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }

  return btoa(binary);
};

const loadAndAddFont = async (pdfDoc, fontUrl, fontNameTTF, fontName, fontStyle) => {
  try {
    const response = await fetch(fontUrl);
    const buffer = await response.arrayBuffer();
    const base64 = arrayBufferToBase64(buffer);

    pdfDoc.addFileToVFS(`${fontNameTTF}.ttf`, base64);
    pdfDoc.addFont(`${fontNameTTF}.ttf`, fontName, fontStyle);
  } catch (error) {
    console.error(`Font loading failed: ${fontName}`, error);
  }
};

// -----------------------------------------------------------------------------
// HEADER / FOOTER (border, company name, form title, logo, page numbers)
// -----------------------------------------------------------------------------

const addPageDecorators = (doc, totalPages, data = {}) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  for (let page = 1; page <= totalPages; page++) {
    doc.setPage(page);

    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.7);
    doc.rect(
      config.borderMargin,
      config.borderMargin,
      pageWidth - config.borderMargin * 2,
      pageHeight - config.borderMargin * 2
    );

    doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
    doc.setFontSize(18);
    doc.setTextColor(25, 25, 25);
    doc.text(
      data.companyTitle || "DYC GLOBAL PVT. LTD.",
      pageWidth / 2,
      config.borderMargin + 25,
      { align: "center" }
    );

    doc.setFontSize(14);
    doc.text("INTERVIEW ASSESSMENT FORM", pageWidth / 2, config.borderMargin + 45, {
      align: "center",
    });

    try {
      const logoSize = 45;
      const logoX = pageWidth - config.borderMargin - logoSize - 8;
      const logoY = config.borderMargin + 5;

      try {
        doc.addImage(DYC_LOGO, "WEBP", logoX, logoY, logoSize, logoSize);
      } catch (_) {
        try {
          doc.addImage(DYC_LOGO, "PNG", logoX, logoY, logoSize, logoSize);
        } catch (__) {
          doc.addImage(DYC_LOGO, logoX, logoY, logoSize, logoSize);
        }
      }
    } catch (_) {
      // logo optional
    }

    doc.setLineWidth(0.5);
    doc.line(
      config.borderMargin,
      config.borderMargin + 55,
      pageWidth - config.borderMargin,
      config.borderMargin + 55
    );

    doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
    doc.setFontSize(7.5);
    doc.text(
      "Company General Use. All Rights Reserved By DYC Global Pvt. Ltd.",
      config.borderMargin + 8,
      pageHeight - config.borderMargin - 8
    );

    doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
    doc.text(
      `Page ${page} of ${totalPages}`,
      pageWidth - config.borderMargin - 60,
      pageHeight - config.borderMargin - 8
    );
  }
};

// -----------------------------------------------------------------------------
// CHECKBOX / CHECKMARK PRIMITIVES
// -----------------------------------------------------------------------------

// Empty square box, ticked with a checkmark when `checked` is true.
// Used for Recommendations & Grade (form always shows the box outline).
const drawCheckbox = (doc, x, y, checked = false, label = "", fontSize = 9) => {
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.7);
  doc.rect(x, y - 7, 8, 8);

  if (checked) {
    doc.setLineWidth(1.1);
    doc.line(x + 1.5, y - 3, x + 3.5, y - 1);
    doc.line(x + 3.5, y - 1, x + 7, y - 6);
    doc.setLineWidth(0.7);
  }

  if (label) {
    doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
    doc.setFontSize(fontSize);
    doc.text(label, x + 14, y);
  }
};

// Bare checkmark with no box outline — used inside rating-table cells,
// matching the attached PDF where selected cells just carry a tick.
const drawCheckmark = (doc, centerX, centerY, size = 9) => {
  doc.setDrawColor(20, 20, 120);
  doc.setLineWidth(1.2);
  doc.line(centerX - size / 2.2, centerY, centerX - size / 6, centerY + size / 2.6);
  doc.line(centerX - size / 6, centerY + size / 2.6, centerX + size / 1.8, centerY - size / 2.2);
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.7);
};

// -----------------------------------------------------------------------------
// SECTION TITLE / LABEL helpers
// -----------------------------------------------------------------------------

const drawSectionTitle = (doc, title, y) => {
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(10.5);
  doc.setTextColor(0, 0, 0);
  doc.text(title, config.margin, y);
};

// -----------------------------------------------------------------------------
// TOP CANDIDATE INFO GRID
// (POSITION APPLIED FOR / DATE / PLACE / NAME / EXP / ORG / DESIGNATION / EDU / OTHERS)
// -----------------------------------------------------------------------------

const drawCandidateInfoTable = (doc, data) => {
  const labelStyle = { ...getHeaderStyles(), halign: "left", fontSize: config.fontSizeSm };
  const valueStyle = { ...getBaseStyles(), halign: "left" };

  const cell = (content, styles) => ({ content: String(content || ""), styles });
  const spanCell = (content, styles, colSpan) => ({
    content: String(content || ""),
    styles,
    colSpan,
  });

  const rows = [
    [
      cell("POSITION APPLIED\nFOR", labelStyle),
      cell(data.positionAppliedFor, valueStyle),
      cell("DATE OF\nINTERVIEW", labelStyle),
      cell(data.dateOfInterview, valueStyle),
      cell("PLACE OF\nINTERVIEW", labelStyle),
      cell(data.placeOfInterview, valueStyle),
    ],
    [
      cell("NAME OF THE\nCANDIDATE", labelStyle),
      spanCell(data.nameOfTheCandidate, valueStyle, 3),
      cell("TOTAL RELEVANT\nWORK EXP.", labelStyle),
      cell(data.totalRelevantExperience, valueStyle),
    ],
    [
      cell("NAME OF CURRENT\nORGANISATION", labelStyle),
      spanCell(data.nameOfCurrentOrganization, valueStyle, 3),
      cell("CURRENT\nDESIGNATION", labelStyle),
      cell(data.currentDesignation, valueStyle),
    ],
    [
      cell("EDUCATIONAL\nQUALIFICATIONS", labelStyle),
      spanCell(data.educationQualification, valueStyle, 3),
      cell("OTHERS\n(IF ANY)", labelStyle),
      cell(data.othersIfAny, valueStyle),
    ],
  ];

  const colWidth = layout.contentWidth / 6;

  autoTable(doc, {
    startY: layout.y,
    body: rows,
    theme: "grid",
    styles: { ...getBaseStyles(), minCellHeight: 24 },
    margin: {
      top: config.contentTop,
      bottom: config.contentBottom,
      left: config.margin,
      right: config.margin,
    },
    columnStyles: {
      0: { cellWidth: colWidth },
      1: { cellWidth: colWidth },
      2: { cellWidth: colWidth },
      3: { cellWidth: colWidth },
      4: { cellWidth: colWidth },
      5: { cellWidth: colWidth },
    },
  });

  layout.afterTable();
};

// -----------------------------------------------------------------------------
// RATINGS TABLE (shared by the technical & behavioural sections)
// -----------------------------------------------------------------------------

const drawRatingsTable = (doc, headingLabel, rowsConfig, data) => {
  layout.ensureSpace(90);

  const attrColWidth = layout.contentWidth * 0.32;
  const remarksColWidth = layout.contentWidth * 0.16;
  const ratingColWidth = (layout.contentWidth - attrColWidth - remarksColWidth) / RATING_COLUMNS.length;

  const head = [
    [
      { content: headingLabel, styles: getHeaderStyles() },
      ...RATING_COLUMNS.map((col) => ({ content: col.key, styles: getHeaderStyles() })),
      { content: "REMARKS", styles: getHeaderStyles() },
    ],
    [
      { content: "ATTRIBUTES", styles: { ...getSubHeaderStyles(), halign: "left" } },
      ...RATING_COLUMNS.map((col) => ({ content: col.sub, styles: getSubHeaderStyles() })),
      { content: "", styles: getSubHeaderStyles() },
    ],
  ];

  const body = rowsConfig.map((row) => {
    const rowCells = [
      {
        content: `${row.label}\n${row.description}`,
        rowData: row,
        styles: { ...getBaseStyles(), fontSize: config.fontSizeSm, valign: "top" },
      },
    ];

    RATING_COLUMNS.forEach((col) => {
      rowCells.push({
        content: "",
        rowData: row,
        ratingKey: col.key,
        styles: { ...getBaseStyles(), halign: "center" },
      });
    });

    rowCells.push({
      content: data[row.remarkKey] || "",
      styles: { ...getBaseStyles(), fontSize: config.fontSizeSm, valign: "top" },
    });

    return rowCells;
  });

  autoTable(doc, {
    startY: layout.y,
    head,
    body,
    theme: "grid",
    styles: { ...getBaseStyles(), minCellHeight: 26 },
    margin: {
      top: config.contentTop,
      bottom: config.contentBottom,
      left: config.margin,
      right: config.margin,
    },
    columnStyles: {
      0: { cellWidth: attrColWidth },
      1: { cellWidth: ratingColWidth },
      2: { cellWidth: ratingColWidth },
      3: { cellWidth: ratingColWidth },
      4: { cellWidth: ratingColWidth },
      5: { cellWidth: remarksColWidth },
    },
    didParseCell: (hookData) => {
      // IMPORTANT: hookData.cell.width/padding are NOT finalized yet at this
      // stage (column layout runs after didParseCell). Using them here
      // produces a near-zero width, which makes splitTextToSize wrap one
      // character per line (the "vertical text" bug). Use the column width
      // we already computed ourselves (attrColWidth) instead.
      if (hookData.section === "body" && hookData.column.index === 0) {
        const raw = hookData.cell.raw;
        if (raw?.rowData) {
          const cellPaddingH = config.cellPadding * 2;
          const availableWidth = attrColWidth - cellPaddingH;
          hookData.cell.text = doc.splitTextToSize(
            `${raw.rowData.label}\n${raw.rowData.description}`,
            availableWidth
          );
        }
      }
    },
    didDrawCell: (hookData) => {
      if (hookData.section !== "body") return;

      const raw = hookData.cell.raw;
      if (!raw || !raw.ratingKey) return;

      const isSelected = data[raw.rowData.stateKey] === raw.ratingKey;
      if (isSelected) {
        const centerX = hookData.cell.x + hookData.cell.width / 2;
        const centerY = hookData.cell.y + hookData.cell.height / 2;
        drawCheckmark(doc, centerX, centerY, 11);
      }
    },
  });

  layout.afterTable();
};

// -----------------------------------------------------------------------------
// "WAS INTERVIEWED WITHIN LAST YEAR" LINE
// -----------------------------------------------------------------------------

const drawInterviewedLastYear = (doc, data) => {
  layout.ensureSpace(60);

  const wasInterviewed = data.wasInterviewedLastYear || "";

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(config.fontSize);
  doc.setTextColor(0, 0, 0);

  const label = "Was the Candidate Interviewed by DYC Global Within Last 1 Year: Yes/No (";
  doc.text(label, config.margin, layout.y);

  const labelWidth = doc.getTextWidth(label);
  doc.text(`${wasInterviewed}`, config.margin + labelWidth + 4, layout.y);
  doc.text(")", config.margin + labelWidth + 22, layout.y);

  layout.advance(config.lineGap);

  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(
    `If yes then, Location: ${data.interviewedLastYearLocation || "-"}`,
    config.margin,
    layout.y
  );
  doc.text(
    `Position: ${data.interviewedLastYearPosition || "-"}`,
    config.margin + 260,
    layout.y
  );

  layout.advance(config.lineGap);
  doc.text(`Date of interview: ${data.interviewedLastYearDate || "-"}`, config.margin, layout.y);

  layout.advance(config.lineGap + 6);
};

// -----------------------------------------------------------------------------
// OVERALL COMMENTS BOX
// -----------------------------------------------------------------------------

const drawOverallComments = (doc, data) => {
  layout.ensureSpace(110);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(config.fontSize);
  doc.text("OVERALL COMMENTS: ", config.margin, layout.y);
  const boldWidth = doc.getTextWidth("OVERALL COMMENTS: ");

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text(
    "Final comments and recommendations for proceeding with the candidate/rejection",
    config.margin + boldWidth,
    layout.y
  );

  layout.advance(14);

  autoTable(doc, {
    startY: layout.y,
    body: [[{ content: data.overallComments || "", styles: { ...getBaseStyles(), valign: "top" } }]],
    theme: "grid",
    styles: { ...getBaseStyles(), minCellHeight: 70 },
    margin: {
      top: config.contentTop,
      bottom: config.contentBottom,
      left: config.margin,
      right: config.margin,
    },
    columnStyles: {
      0: { cellWidth: layout.contentWidth },
    },
  });

  layout.afterTable();
};

// -----------------------------------------------------------------------------
// JOB FIT & MOBILITY TABLE
// -----------------------------------------------------------------------------

const drawJobFitAndMobility = (doc, data) => {
  layout.ensureSpace(70);

  const labelColWidth = layout.contentWidth * 0.28;
  const remarksColWidth = layout.contentWidth * 0.24;
  const optionColWidth = (layout.contentWidth - labelColWidth - remarksColWidth) / 3;

  const body = [
    [
      {
        content: "JOB FIT: (Suitability to occupy the current job in its fullness)",
        styles: { ...getBaseStyles(), fontStyle: customFontFamily.bold, valign: "top" },
      },
      ...JOB_FIT_OPTIONS.map((opt) => ({
        content: opt,
        jobFitOption: opt,
        styles: { ...getBaseStyles(), halign: "center" },
      })),
      { content: data.jobFitRemarks || "", styles: getBaseStyles() },
    ],
    [
      {
        content: "MOBILITY: Is candidate willing to relocate within or outside state?",
        styles: { ...getBaseStyles(), fontStyle: customFontFamily.bold, valign: "top" },
      },
      {
        content: `YES / NO\n${data.mobility || ""}`,
        colSpan: 3,
        styles: { ...getBaseStyles(), halign: "center" },
      },
      { content: data.mobilityRemarks || "", styles: getBaseStyles() },
    ],
  ];

  autoTable(doc, {
    startY: layout.y,
    body,
    theme: "grid",
    styles: { ...getBaseStyles(), minCellHeight: 30 },
    margin: {
      top: config.contentTop,
      bottom: config.contentBottom,
      left: config.margin,
      right: config.margin,
    },
    columnStyles: {
      0: { cellWidth: labelColWidth },
      1: { cellWidth: optionColWidth },
      2: { cellWidth: optionColWidth },
      3: { cellWidth: optionColWidth },
      4: { cellWidth: remarksColWidth },
    },
    didDrawCell: (hookData) => {
      if (hookData.section !== "body") return;
      const raw = hookData.cell.raw;
      if (!raw || !raw.jobFitOption) return;

      if (data.jobFit === raw.jobFitOption) {
        const centerX = hookData.cell.x + hookData.cell.width / 2;
        const centerY = hookData.cell.y + hookData.cell.height - 8;
        drawCheckmark(doc, centerX, centerY, 10);
      }
    },
  });

  layout.afterTable();
};

// -----------------------------------------------------------------------------
// RECOMMENDATIONS + GRADE
// -----------------------------------------------------------------------------

const drawRecommendations = (doc, data) => {
  layout.ensureSpace(140);

  drawSectionTitle(doc, "RECOMMENDATIONS:", layout.y);
  layout.advance(22);

  const startX = config.margin;
  const spacing = layout.contentWidth / RECOMMENDATION_OPTIONS.length;

  RECOMMENDATION_OPTIONS.forEach((opt, idx) => {
    const x = startX + idx * spacing;
    drawCheckbox(doc, x, layout.y, data.recommendation === opt.key, opt.label, config.fontSize);
  });

  layout.advance(26);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(config.fontSize);
  doc.text("If Selected: Recommended level of Authorization (Grade - A/B/C/D)", config.margin, layout.y);
  layout.advance(20);

  GRADE_OPTIONS.forEach((grade) => {
    const isChecked = data.recommendation === "Selected" && data.selectedGrade === grade.key;
    drawCheckbox(doc, config.margin, layout.y, isChecked, grade.label, config.fontSizeSm);
    layout.advance(config.lineGap);
  });

  layout.advance(6);
};

// -----------------------------------------------------------------------------
// PANEL MEMBERS TABLE
// -----------------------------------------------------------------------------

const drawPanelMembersTable = (doc, data) => {
  layout.ensureSpace(120);

  const members = data.panelMembers && data.panelMembers.length ? data.panelMembers : [];
  const rows = [];

for (let i = 0; i < PANEL_ROWS; i++) {
  const member = members[i] || { name: "", signatureDate: "" };
  rows.push([
    String(i + 1),
    member.name || "",
    member.signatureDate ? moment(member.signatureDate).format("DD/MM/YYYY") : ""
  ]);
}

  autoTable(doc, {
    startY: layout.y,
    head: [["SR. NO.", "NAME OF PANEL MEMBERS", "SIGNATURE & DATE"]],
    body: rows,
    theme: "grid",
    styles: { ...getBaseStyles(), minCellHeight: 24 },
    headStyles: getHeaderStyles(),
    margin: {
      top: config.contentTop,
      bottom: config.contentBottom,
      left: config.margin,
      right: config.margin,
    },
    columnStyles: {
      0: { cellWidth: layout.contentWidth * 0.1, halign: "center" },
      1: { cellWidth: layout.contentWidth * 0.45 },
      2: { cellWidth: layout.contentWidth * 0.45 },
    },
  });

  layout.afterTable();
};

// -----------------------------------------------------------------------------
// MAIN PDF GENERATOR
// -----------------------------------------------------------------------------

export const generateInterviewAssessmentPDF = async (data = {}) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "a4",
    compress: true,
  });

  await Promise.all([
    loadAndAddFont(doc, ArialNormal, "Arial-Normal", customFontFamily.normalFont, customFontFamily.normal),
    loadAndAddFont(doc, ArialBold, "Arial-Bold", customFontFamily.boldFont, customFontFamily.bold),
    loadAndAddFont(doc, ArialItalic, "Arial-Italic", customFontFamily.italicFont, customFontFamily.italic),
    loadAndAddFont(doc, ArialBoldItalic, "Arial-BoldItalic", customFontFamily.normalFont, "bolditalic"),
  ]);

  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);

  layout.init(doc);
  layout.y = config.contentTop + 10;

  // Page 1: candidate info + technical ratings
  drawCandidateInfoTable(doc, data);
  drawRatingsTable(doc, "RATINGS", TECHNICAL_ATTRIBUTE_ROWS, data);

  // Page 2: behavioural ratings + interviewed-last-year + overall comments
  layout.ensureSpace(200);
  drawRatingsTable(doc, "RATINGS", BEHAVIOURAL_ATTRIBUTE_ROWS, data);

  doc.setFont(customFontFamily.italicFont, customFontFamily.italic);
  doc.setFontSize(config.fontSizeSm);
  doc.text(
    "* EVALUATION OF THESE COMPETENCIES IS MANDATORY FOR PEOPLE MANAGEMENT ROLES",
    config.margin,
    layout.y
  );
  layout.advance(config.sectionGap);

  drawInterviewedLastYear(doc, data);
  drawOverallComments(doc, data);

  // Page 3: job fit / mobility / recommendations / grade / panel
  layout.ensureSpace(250);
  drawJobFitAndMobility(doc, data);
  drawRecommendations(doc, data);
  drawPanelMembersTable(doc, data);

  const totalPages = doc.internal.getNumberOfPages();
  addPageDecorators(doc, totalPages, data);

  return doc;
};

export default generateInterviewAssessmentPDF;